import React, {useCallback, useEffect, useState} from 'react';
import {view, Icon, Action, TextField, Skeleton} from '@sail/ui';
import {cancel, edit} from '@sail/icons/react/Icon';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {
  formatCurrencyAmountToMajorUnitsFixed,
  getDepositAmount,
  isUsdtAssetCode,
  upper,
} from 'src/utils/helpers';
import {getStore, setValue, useSelector} from 'src/v2/state/store';
import {
  BaseCard,
  QrCode,
  Text,
  BrandedButton,
  Banner,
} from 'src/v2/components/common';
import {LabelValueCopyRow} from 'src/v2/components/common/LabelValueCopyRow';
import {RefundMethodSelector} from 'src/v2/components/directDeposit/RefundMethodSelector';
import {RefundAddressDialog} from 'src/v2/components/directDeposit/RefundAddressDialog';
import {
  useDirectDeposit,
  useDirectDepositOptions,
} from 'src/v2/components/directDeposit/useDirectDeposit';
import {NetworkCurrencySelectors} from 'src/v2/components/directDeposit/NetworkCurrencySelectors';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {useDirectDepositPaymentPolling} from 'src/v2/components/directDeposit/useDirectDepositPaymentPolling';
import {useUpdateCustomerEmail} from 'src/v2/components/directDeposit/useUpdateCustomerEmail';
import {useUpdateRefundAddress} from 'src/v2/components/directDeposit/useUpdateRefundAddress';
import {
  handleError,
  clearErrorBanner,
} from 'src/v2/components/error/handleError';
import {ErrorBanner} from 'src/v2/components/error/ErrorBanner';
import {COLORS} from 'src/v2/util/color';
import type {RefundMethod} from 'src/v2/state/types';
import {messages as refundMessages} from 'src/v2/components/directDeposit/refundAddressMessages';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

type Step = 'email' | 'qrCode';

const messages = defineMessages({
  title: {
    id: 'crypto.payins.v2.direct_deposit.title',
    description: 'Title for direct deposit screen',
    defaultMessage: 'Direct deposit via QR code',
  },
  emailLabel: {
    id: 'crypto.payins.v2.direct_deposit_email.email_label',
    description: 'Label for email address input',
    defaultMessage: 'Email address',
  },
  emailPlaceholder: {
    id: 'crypto.payins.v2.direct_deposit_email.email_placeholder',
    description: 'Placeholder for email address input',
    defaultMessage: 'Enter your email address',
  },
  continueButton: {
    id: 'crypto.payins.v2.direct_deposit_email.continue_button',
    description: 'Continue button text',
    defaultMessage: 'Continue',
  },
  paymentInstruction: {
    id: 'crypto.payins.v2.direct_deposit.payment_instruction',
    description: 'Payment instruction text',
    defaultMessage: 'Pay {amount} {currency} on {network}',
  },
  networkWarning: {
    id: 'crypto.payins.v2.direct_deposit.network_warning',
    description: 'Warning about wrong network or currency',
    defaultMessage:
      'Sending any asset other than {currency} or using any network other than {network} will result in the permanent and irreversible loss of the funds.',
  },
  processingTime: {
    id: 'crypto.payins.v2.direct_deposit.processing_time',
    description: 'Note about processing time',
    defaultMessage: 'Processing may take up to 2 minutes.',
  },
  depositAddress: {
    id: 'crypto.payins.v2.direct_deposit.deposit_address',
    description: 'Label for deposit address',
    defaultMessage: 'Address',
  },
  amount: {
    id: 'crypto.payins.v2.direct_deposit.amount',
    description: 'Label for deposit amount',
    defaultMessage: 'Amount',
  },
  usdtExchangeFee: {
    id: 'crypto.payins.v2.heading.usdt_exchange_fee',
    description: 'Disclaimer shown when USDT is selected',
    defaultMessage: 'USDT payments include an exchange fee.',
  },
});

const EmailStep = ({onContinue}: {onContinue: () => void}) => {
  const intl = useIntl();
  const initialEmail = useSelector(
    (state) => state.directDeposit.customerEmail,
  );
  const [updateCustomerEmail, {loading}] = useUpdateCustomerEmail();
  const [updateRefundAddress] = useUpdateRefundAddress();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState(initialEmail || '');

  const {
    selectedNetwork,
    setSelectedNetwork,
    selectedCurrency,
    setSelectedCurrency,
    networkOptions,
    currencyOptions,
  } = useDirectDepositOptions();

  const {refundMethod, customRefundAddress} = useSelector((state) => ({
    refundMethod: state.directDeposit.refundMethod,
    customRefundAddress: state.directDeposit.customRefundAddress,
  }));

  const trimmedEmail = email.trim();
  const isEmailValid =
    trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
  const isButtonDisabled =
    !isEmailValid ||
    !selectedNetwork ||
    !selectedCurrency ||
    refundMethod === null ||
    (refundMethod === 'setNewRefundAddress' && !customRefundAddress.trim()) ||
    isSubmitting;

  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);
    setValue((state) => {
      state.directDeposit.customerEmail = newEmail;
    });
  };

  const handleContinue = async () => {
    if (!selectedNetwork || !refundMethod) return;
    setIsSubmitting(true);
    try {
      await updateRefundAddress({
        refundAddress:
          refundMethod === 'setNewRefundAddress' ? customRefundAddress : null,
        destinationNetwork: selectedNetwork,
      });
      setValue((state) => {
        state.directDeposit.isRefundAddressApplied = true;
      });
      await updateCustomerEmail(email);
      onContinue();
    } catch (error) {
      handleError({error, intl, context: 'direct_deposit'});
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <TextField
        label={intl.formatMessage(messages.emailLabel)}
        placeholder={intl.formatMessage(messages.emailPlaceholder)}
        value={email}
        onChange={(e) => handleEmailChange(e.target.value)}
        type="email"
      />

      <NetworkCurrencySelectors
        selectedNetwork={selectedNetwork}
        setSelectedNetwork={setSelectedNetwork}
        networkOptions={networkOptions}
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        currencyOptions={currencyOptions}
      />

      {selectedNetwork && selectedCurrency ? <RefundMethodSelector /> : null}

      <BrandedButton
        onClick={handleContinue}
        disabled={isButtonDisabled}
        loading={loading || isSubmitting}
      >
        <FormattedMessage {...messages.continueButton} />
      </BrandedButton>
    </>
  );
};

type QrPaymentDetailsProps = {
  selectedNetwork: string;
  selectedCurrency: string;
  depositAddress: string;
  isAllocating: boolean;
  amount: string;
  onCopyDepositAddress: () => void;
  onCopyAmount: () => void;
  intl: ReturnType<typeof useIntl>;
};

const QrPaymentDetails = ({
  selectedNetwork,
  selectedCurrency,
  depositAddress,
  isAllocating,
  amount,
  onCopyDepositAddress,
  onCopyAmount,
  intl,
}: QrPaymentDetailsProps) => (
  <>
    <view.div
      css={{
        stack: 'y',
        alignItems: 'center',
        padding: '24px 12px',
        gap: 'medium',
        background: 'rgba(255, 255, 255, 0.72)',
        border: '1px solid rgba(26, 26, 26, 0.05)',
        borderRadius: 'small',
      }}
    >
      <Text type="bodyEmphasized" css={{textAlign: 'center'}}>
        <FormattedMessage
          {...messages.paymentInstruction}
          values={{
            amount,
            currency: formatAssetCode(selectedCurrency),
            network: upper(selectedNetwork),
          }}
        />
      </Text>

      {isUsdtAssetCode(selectedCurrency) ? (
        <Text
          type="caption"
          css={{textAlign: 'center', color: 'rgba(26, 26, 26, 0.5)'}}
        >
          <FormattedMessage {...messages.usdtExchangeFee} />
        </Text>
      ) : null}

      <view.div
        css={{
          width: '208px',
          height: '207px',
          background: '#FFFFFF',
          borderRadius: 'medium',
          stack: 'y',
          alignX: 'center',
          alignY: 'center',
          padding: 'xsmall',
        }}
      >
        {isAllocating ? (
          <Skeleton css={{width: '200px', height: '200px'}} />
        ) : (
          <QrCode qrCodeUri={depositAddress} size={200} />
        )}
      </view.div>

      <Banner
        variant="warning"
        message={
          <FormattedMessage
            {...messages.networkWarning}
            values={{
              currency: formatAssetCode(selectedCurrency),
              network: upper(selectedNetwork),
            }}
          />
        }
      />

      <Text
        type="caption"
        css={{textAlign: 'center', color: COLORS.textDisabled}}
      >
        <FormattedMessage {...messages.processingTime} />
      </Text>
    </view.div>

    <view.div css={{stack: 'y', gapY: 'small', paddingY: 'small'}}>
      <LabelValueCopyRow
        label={intl.formatMessage(messages.depositAddress)}
        value={depositAddress}
        onCopy={onCopyDepositAddress}
        loading={isAllocating}
        contentContainerStyle={{paddingRight: 'small'}}
      />
      <LabelValueCopyRow
        label={intl.formatMessage(messages.amount)}
        value={amount}
        onCopy={onCopyAmount}
        contentContainerStyle={{paddingRight: 'small'}}
      />
    </view.div>
  </>
);

const QrCodeStep = () => {
  const intl = useIntl();
  const {
    selectedNetwork,
    setSelectedNetwork,
    selectedCurrency,
    setSelectedCurrency,
    networkOptions,
    currencyOptions,
    depositAddress,
    isAllocating,
  } = useDirectDeposit();

  const {startPolling} = useDirectDepositPaymentPolling('QR_CODE');

  const store = getStore();
  const depositAmount = store.useSelector(
    (state) => state.paymentDetails.amount,
  );
  const depositCurrency = store.useSelector(
    (state) => state.paymentDetails.currency,
  );
  const initialIsApplied = store.useSelector(
    (state) => state.directDeposit.isRefundAddressApplied,
  );

  const [qrCodeVisible, setQrCodeVisible] = useState(initialIsApplied);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [updateRefundAddress, {loading: updateRefundLoading}] =
    useUpdateRefundAddress();

  const {refundMethod: refundMethodQr, customRefundAddress} = store.useSelector(
    (state) => ({
      refundMethod: state.directDeposit.refundMethod,
      customRefundAddress: state.directDeposit.customRefundAddress,
    }),
  );

  useEffect(() => {
    if (refundMethodQr === null) {
      setQrCodeVisible(false);
    }
  }, [refundMethodQr]);

  useEffect(() => {
    startPolling();
  }, [startPolling]);

  const adjustedDepositAmount = getDepositAmount(
    depositAmount,
    selectedCurrency ?? '',
  );
  const amount = formatCurrencyAmountToMajorUnitsFixed(
    adjustedDepositAmount,
    depositCurrency,
  );

  const handleCopyDepositAddress = useCallback(() => {
    navigator.clipboard.writeText(depositAddress);
  }, [depositAddress]);

  const handleCopyAmount = useCallback(() => {
    navigator.clipboard.writeText(amount);
  }, [amount]);

  const handleContinue = async () => {
    if (!selectedNetwork || !refundMethodQr) return;
    setIsSubmitting(true);
    try {
      if (refundMethodQr === 'setNewRefundAddress') {
        await updateRefundAddress({
          refundAddress: customRefundAddress,
          destinationNetwork: selectedNetwork,
        });
        store.setValue((state) => {
          state.directDeposit.isRefundAddressApplied = true;
        });
      } else {
        await updateRefundAddress({
          refundAddress: null,
          destinationNetwork: selectedNetwork,
        });
        store.setValue((state) => {
          state.directDeposit.isRefundAddressApplied = true;
        });
      }
      clearErrorBanner();
      setQrCodeVisible(true);
    } catch (error) {
      handleError({error, intl, context: 'direct_deposit'});
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditApply = async ({
    refundMethod: newRefundMethod,
    customRefundAddress: newAddress,
  }: {
    refundMethod: RefundMethod;
    customRefundAddress: string;
  }) => {
    if (!selectedNetwork) return;
    try {
      if (newRefundMethod === 'setNewRefundAddress') {
        await updateRefundAddress({
          refundAddress: newAddress,
          destinationNetwork: selectedNetwork,
        });
        store.setValue((state) => {
          state.directDeposit.refundMethod = 'setNewRefundAddress';
          state.directDeposit.customRefundAddress = newAddress;
          state.directDeposit.isRefundAddressApplied = true;
        });
      } else {
        await updateRefundAddress({
          refundAddress: null,
          destinationNetwork: selectedNetwork,
        });
        store.setValue((state) => {
          state.directDeposit.refundMethod = 'sendToPaymentAddress';
          state.directDeposit.customRefundAddress = '';
          state.directDeposit.isRefundAddressApplied = true;
        });
      }
      setIsEditDialogOpen(false);
    } catch (error) {
      handleError({error, intl, context: 'direct_deposit'});
      throw error;
    }
  };

  const isContinueDisabled =
    refundMethodQr === null ||
    (refundMethodQr === 'setNewRefundAddress' && !customRefundAddress.trim()) ||
    isSubmitting;

  const refundMethodDisplayValue =
    refundMethodQr === 'sendToPaymentAddress'
      ? intl.formatMessage(refundMessages.sendToPaymentAddress)
      : customRefundAddress;

  if (!selectedNetwork || !selectedCurrency) {
    return null;
  }

  return (
    <>
      <NetworkCurrencySelectors
        selectedNetwork={selectedNetwork}
        setSelectedNetwork={setSelectedNetwork}
        networkOptions={networkOptions}
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        currencyOptions={currencyOptions}
      />

      {qrCodeVisible ? (
        <>
          <QrPaymentDetails
            selectedNetwork={selectedNetwork}
            selectedCurrency={selectedCurrency}
            depositAddress={depositAddress}
            isAllocating={isAllocating}
            amount={amount}
            onCopyDepositAddress={handleCopyDepositAddress}
            onCopyAmount={handleCopyAmount}
            intl={intl}
          />

          <view.div css={{stack: 'y', gapY: 'small', paddingY: 'small'}}>
            <view.div css={{stack: 'y', gap: 'xsmall'}}>
              <Text type="body" css={{color: COLORS.textDisabled}}>
                <FormattedMessage {...refundMessages.preferredRefundMethod} />
              </Text>
              <view.div
                css={{
                  stack: 'x',
                  distribute: 'space-between',
                  alignItems: 'flex-start',
                  gap: 'medium',
                  paddingRight: 'small',
                }}
              >
                <Text
                  type="bodyEmphasized"
                  css={{
                    wordBreak: 'break-all',
                    color: COLORS.textPrimary,
                    flex: 1,
                  }}
                >
                  {refundMethodDisplayValue}
                </Text>
                <Action onPress={() => setIsEditDialogOpen(true)}>
                  <Icon
                    icon={edit}
                    size="small"
                    css={{cursor: 'pointer', flexShrink: 0}}
                  />
                </Action>
              </view.div>
            </view.div>
          </view.div>

          {selectedNetwork ? (
            <RefundAddressDialog
              isOpen={isEditDialogOpen}
              onApply={handleEditApply}
              initialCustomRefundAddress={customRefundAddress}
              initialRefundMethod={refundMethodQr}
              selectedNetwork={selectedNetwork}
              loading={updateRefundLoading}
            />
          ) : null}
        </>
      ) : (
        <>
          <RefundMethodSelector />
          <BrandedButton
            onClick={handleContinue}
            disabled={isContinueDisabled}
            loading={isSubmitting}
          >
            <FormattedMessage {...messages.continueButton} />
          </BrandedButton>
        </>
      )}
    </>
  );
};

export const DirectDepositScreen = () => {
  const [step, setStep] = useState<Step>('email');

  const handleClose = useCallback(() => {
    setValue((state) => {
      state.screen = 'WALLET_SELECT_GRID';
    });
  }, []);

  const handleContinueToQrCode = useCallback(() => {
    setStep('qrCode');
  }, []);

  const header = (
    <>
      <Text type="bodyEmphasized">
        <FormattedMessage {...messages.title} />
      </Text>

      <Action onPress={handleClose}>
        <view.div css={{cursor: 'pointer', padding: 'xsmall'}}>
          <Icon icon={cancel} size="xsmall" />
        </view.div>
      </Action>
    </>
  );

  return (
    <BaseCard header={header}>
      <ErrorBanner />
      {step === 'email' ? (
        <EmailStep onContinue={handleContinueToQrCode} />
      ) : (
        <QrCodeStep />
      )}
    </BaseCard>
  );
};
